/* =========================================================================
 * THE GPT BRIDGE — the network half, in the page.
 *
 * The page never touches ChatGPT. It talks to the SR GPT Bridge extension
 * through window.postMessage (the extension's content script on this site
 * relays to its background worker, which drives the ChatGPT tab). The page
 * holds no ChatGPT credential and the extension reads none: it works because
 * the ChatGPT tab is Brian's own, signed in, in his own browser.
 *
 * The image comes back as bytes and goes out through the SAME owned-asset
 * upload this app already uses for crests (features/artwork/ownedAsset.js),
 * so it lands in HubSpot File Manager and never as a link to OpenAI's servers.
 * Nothing here calls a billable endpoint.
 * ========================================================================= */
import { blobToDataUrl, uploadOwned, OWNED_TYPES, OWNED_MAX_BYTES } from '../ownedAsset.js'
import { getBuild, advanceBuildStatus } from '../buildApi.js'
import { GPT_BRIDGE, bridgeFileName, provenanceNote } from './bridgeModel.js'
import { zipStore } from './zipStore.js'

const FROM_PAGE = 'sr-app'
const FROM_EXT = 'sr-gpt-bridge'
const RUN_TIMEOUT_MS = 6 * 60 * 1000

let seq = 0
const nextId = () => `job-${Date.now()}-${++seq}`

/** Is the extension running in this browser? Resolves { present, version }. Never rejects. */
export function detectBridge(timeoutMs = 900) {
  if (typeof window === 'undefined') return Promise.resolve({ present: false })
  return new Promise((resolve) => {
    const id = nextId()
    const done = (v) => { window.removeEventListener('message', on); clearTimeout(t); resolve(v) }
    const on = (e) => {
      if (e.source !== window || e.data?.source !== FROM_EXT) return
      if (e.data.type === 'SR_GPT_BRIDGE_READY' && (!e.data.id || e.data.id === id)) {
        done({ present: true, version: String(e.data.version || '') })
      }
    }
    window.addEventListener('message', on)
    const t = setTimeout(() => done({ present: false }), timeoutMs)
    window.postMessage({ source: FROM_PAGE, type: 'SR_GPT_BRIDGE_PING', id }, window.location.origin)
  })
}

/**
 * Run one job in the GPT. Resolves { ok:true, dataUrl, mime, chatUrl } or
 * { ok:false, code, detail, step }. Never rejects: every failure is a value
 * the panel can show beside the manual path.
 */
export function runInGpt({ gptUrl, prompt, references }, onProgress) {
  if (typeof window === 'undefined') return Promise.resolve({ ok: false, code: 'no_extension' })
  return new Promise((resolve) => {
    const id = nextId()
    const finish = (v) => { window.removeEventListener('message', on); clearTimeout(t); resolve(v) }
    const on = (e) => {
      if (e.source !== window || e.data?.source !== FROM_EXT || e.data.id !== id) return
      if (e.data.type === 'SR_GPT_BRIDGE_PROGRESS') onProgress?.(e.data.step, e.data.detail || '')
      if (e.data.type === 'SR_GPT_BRIDGE_RESULT') {
        finish(e.data.ok
          ? {
            ok: true, dataUrl: e.data.dataUrl, mime: e.data.mime || '', chatUrl: e.data.chatUrl || '',
            /* The verified size of what was imported. Carried so the panel can
             * say "1672 x 941, 2.3 MB" rather than leaving the operator to
             * assume the full file arrived. */
            width: e.data.width || 0, height: e.data.height || 0,
            byteLength: e.data.byteLength || 0, importRoute: e.data.route || '',
          }
          : { ok: false, code: e.data.code || 'unknown', detail: e.data.detail || '', step: e.data.step || '' })
      }
    }
    window.addEventListener('message', on)
    const t = setTimeout(() => {
      window.postMessage({ source: FROM_PAGE, type: 'SR_GPT_BRIDGE_CANCEL', id }, window.location.origin)
      finish({ ok: false, code: 'timeout', detail: `${Math.round(RUN_TIMEOUT_MS / 60000)} minutes` })
    }, RUN_TIMEOUT_MS)
    window.postMessage({
      source: FROM_PAGE, type: 'SR_GPT_BRIDGE_RUN', id,
      gptUrl, prompt,
      // URLs only. The extension fetches them itself, so nothing large crosses postMessage twice.
      references: (references || []).map((r) => ({ name: r.name, url: new URL(r.url, window.location.href).href })),
    }, window.location.origin)
  })
}

/**
 * The references for one job: the configured set, plus the job's owned crest
 * when asked.
 *
 * ⚠️ THE URLS COME BACK ABSOLUTE, AND THAT IS THE FIX FOR A LIVE DEFECT.
 *
 * Found walking the chain on 2026-09-18. `referenceSet` in
 * sr-runtime-config.json holds SITE-RELATIVE urls, because that is the natural
 * way to point at `/reference/sr-housings-rs.jpg` on this very site. The wire
 * already resolved them (`runInGpt` posts `new URL(r.url, location.href).href`)
 * so the extension was happy. The APP-SIDE pre-flight was not: it tested the
 * raw `/reference/…` string against a rule that requires an absolute URL,
 * decided SR's own housing reference was not SR-owned, disabled the one-press
 * button and told the operator to "attach the mark from your device" — advice
 * that had nothing to do with the file it was complaining about.
 *
 * Two places normalising the same value differently is the same class of defect
 * as the two allowlists in ownedHosts.js. So normalisation happens ONCE, here,
 * and every consumer sees exactly the string that will be sent.
 */
export function jobReferences(config, crestUrl) {
  const refs = [...(config?.referenceSet || [])]
  if (config?.attachJobCrest && crestUrl) refs.push({ name: 'job-crest' + (/\.(png|jpe?g|webp|svg)(\?|$)/i.exec(crestUrl)?.[0]?.replace(/\?$/, '') || '.png'), url: crestUrl })
  const base = typeof window !== 'undefined' ? window.location.href : 'https://app.srchamp.com/'
  return refs.map((r) => {
    let url = String(r.url || '')
    try { url = new URL(url, base).href } catch { /* leave it; the rule will refuse it */ }
    return { ...r, url }
  })
}

/** Fetch every reference and hand back one .zip to save. */
export async function downloadReferenceZip(refs, zipName = 'sr-gpt-references.zip') {
  const files = []
  for (const r of refs) {
    const res = await fetch(r.url, { credentials: 'omit', cache: 'no-store' })
    if (!res.ok) throw new Error(`${r.name} answered ${res.status}`)
    files.push({ name: r.name, bytes: new Uint8Array(await res.arrayBuffer()) })
  }
  const blob = new Blob([zipStore(files)], { type: 'application/zip' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = zipName
  document.body.appendChild(a)
  a.click()
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 2000)
  return files.length
}

/**
 * Keep an image inside what the upload route accepts. A ChatGPT PNG can pass
 * the 6MB cap; re-encode it as JPEG rather than refuse it in front of a room.
 */
async function fitForUpload(blob) {
  const type = String(blob.type || '').toLowerCase()
  if (OWNED_TYPES.includes(type) && blob.size <= OWNED_MAX_BYTES * 0.92) return blob
  const bmp = await createImageBitmap(blob)
  const canvas = document.createElement('canvas')
  canvas.width = bmp.width; canvas.height = bmp.height
  canvas.getContext('2d').drawImage(bmp, 0, 0)
  for (const q of [0.92, 0.85, 0.75]) {
    const out = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', q))
    if (out && out.size <= OWNED_MAX_BYTES * 0.92) return out
  }
  throw new Error('The image is too large to upload even as a JPEG.')
}

/**
 * THE RETURN LEG, shared by the automatic and the manual path.
 *
 *  1. Upload to File Manager (kind gpt-bridge), named with its provenance.
 *  2. Read the build's CURRENT status and post it back unchanged with a
 *     provenance note, so the record's history says where the image came from
 *     and when, and the status does not move.
 *
 * Step 2 can be refused (a role without the build-submit permission) without
 * undoing step 1: the file is still owned and its name still says what it is,
 * and the result reports that the record note is missing rather than hiding it.
 */
export async function attachGptImage({ buildId, view, blob, route, gptUrl, chatUrl, preservePixels = false }) {
  const at = new Date().toISOString()
  if(preservePixels && (blob.type !== 'image/png' || blob.size > OWNED_MAX_BYTES * 0.92)) {
    throw new Error('The protected PNG exceeds the lossless upload limit. Use a smaller source PNG; converting this proof to JPEG would change the original housing pixels.')
  }
  const fitted = preservePixels ? blob : await fitForUpload(blob)
  const ext = fitted.type === 'image/jpeg' ? 'jpg' : fitted.type === 'image/webp' ? 'webp' : 'png'
  const owned = await uploadOwned({
    name: bridgeFileName({ buildId, view, at, ext }),
    kind: GPT_BRIDGE.KIND,
    dataUrl: await blobToDataUrl(fitted),
  })
  let record = null
  let recordError = ''
  /* ⚠️ A JOB WITH NO BUILD RECORD IS NOT AN ERROR ANY MORE. Until 2026-09-18
   * this threw on a missing buildId, which was right when the only caller was a
   * ring view of an existing build. A Bezel Drop sheet is not a build — it is
   * one mark across the housing library — so there is no record to note it
   * against, and refusing the upload would have thrown away an owned file over
   * a foreign-key that never existed. The file still lands owned in File
   * Manager and `bridgeFileName` already names it `nobuild-…`, so the
   * provenance survives on the file itself. The absence is REPORTED, never
   * silently dressed up as a successful record write. */
  if (!buildId) {
    recordError = 'there is no build record for this job, so the file name carries the provenance on its own'
    return {
      url: owned.url,
      fileId: owned.fileId || '',
      at,
      view,
      route,
      gpt: gptUrl || '',
      chat: chatUrl || '',
      source: GPT_BRIDGE.SOURCE,
      mark: GPT_BRIDGE.MARK,
      record,
      recordError,
    }
  }
  try {
    const current = await getBuild(buildId)
    const status = current?.record?.status
    if (!status) throw new Error('the build record could not be read')
    const res = await advanceBuildStatus(buildId, {
      status,
      note: provenanceNote({ view, fileId: owned.fileId, url: owned.url, route, at, gptUrl, chatUrl }),
    })
    record = res?.record || null
  } catch (e) {
    recordError = e?.message || 'the build record refused the note'
  }
  return {
    url: owned.url,
    fileId: owned.fileId || '',
    at,
    view,
    route,
    gpt: gptUrl || '',
    chat: chatUrl || '',
    source: GPT_BRIDGE.SOURCE,
    mark: GPT_BRIDGE.MARK,
    record,
    recordError,
  }
}

/** A data: URL from the extension, as a Blob. */
export async function dataUrlToBlob(dataUrl) {
  return (await fetch(dataUrl)).blob()
}
